import { Button } from 'primereact/button';
import React, {
  useState,
  DragEvent,
  ChangeEvent,
  useRef,
  useEffect,
} from 'react';
import { FileUploaderProps } from '../../types';

const FileUploader: React.FC<FileUploaderProps> = ({
  name,
  onInputChange,
  multiple = false,
  acceptedFiles = '.pdf,.doc,.docx,.png,.jpg',
  maxSize = 10,
  value = null,
  showSizeLimit = true
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync with parent component's value
  useEffect(() => {
    if (!value) {
      setFiles([]);
      return;
    }

    if (Array.isArray(value)) {
      // Filter to ensure all items are valid File objects
      setFiles(value.filter((f) => f instanceof File));
    } else if (value instanceof File) {
      setFiles([value]);
    } else {
      setFiles([]);
    }
  }, [value]);

  // Sync with parent component's value
  useEffect(() => {
    if (value) {
      setFiles(Array.isArray(value) ? value : [value]);
    } else {
      setFiles([]);
    }
  }, [value]);

  // Helper functions
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Validate single file (only type validation)
  const validateFile = (file: File): string[] => {
    const fileErrors: string[] = [];

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const allowedExtensions = ['pdf', 'doc', 'docx', 'xml', 'png', 'jpg'];

    if (fileExtension && !allowedExtensions.includes(fileExtension)) {
      fileErrors.push('Invalid file type');
    }

    // Size validation (8MB = 8 * 1024 * 1024 bytes)
    const maxSizeInBytes = 8 * 1024 * 1024; // 8MB
    if (file.size > maxSizeInBytes) {
      fileErrors.push(`File size exceeds 8MB (${formatFileSize(file.size)})`);
    }

    return fileErrors;
  };

  // Process selected files
  const processFiles = (newFiles: File[]) => {
    const validationErrors: string[] = [];
    const validFiles: File[] = [];

    newFiles.forEach((file) => {
      const fileErrors = validateFile(file);
      if (fileErrors.length > 0) {
        validationErrors.push(`${file.name}: ${fileErrors.join(', ')}`);
      } else {
        validFiles.push(file);
      }
    });

    setErrors(validationErrors);

    let updatedFiles: File[];
    if (multiple) {
      // Merge with existing files, avoiding duplicates
      updatedFiles = [...files, ...validFiles].filter(
        (file, index, self) =>
          index ===
          self.findIndex((f) => f.name === file.name && f.size === file.size),
      );
    } else {
      updatedFiles = validFiles.length > 0 ? [validFiles[0]] : [];
    }

    setFiles(updatedFiles);

    // Notify parent
    onInputChange(
      { files: multiple ? updatedFiles : updatedFiles[0] || null },
      name,
    );

    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Event handlers
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) processFiles(Array.from(e.target.files));
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    processFiles(Array.from(e.dataTransfer.files));
  };

  const removeFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    onInputChange(
      { files: multiple ? updatedFiles : updatedFiles[0] || null },
      name,
    );
  };

  const clearFiles = () => {
    setFiles([]);
    setErrors([]);
    onInputChange({ files: multiple ? [] : null }, name);
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName?.split('.')?.pop()?.toLowerCase();
    const icons = {
      pdf: 'pi pi-file-pdf',
      doc: 'pi pi-file-word',
      docx: 'pi pi-file-word',
      jpg: 'pi pi-image',
      // jpeg: "pi pi-image",
      // png: "pi pi-image",
      // gif: "pi pi-image",
      // webp: "pi pi-image",
    };
    return icons[ext as keyof typeof icons] || 'pi pi-file';
  };

  const hasError = (fileName: string) => {
    return errors.some((error) => error.startsWith(fileName));
  };

  const getError = (fileName: string) => {
    const error = errors.find((error) => error.startsWith(fileName));
    return error ? error.split(': ')[1] : '';
  };

  return (
    <div className="file-uploader">
      {/* Drop Zone */}
      <div
        className={`upload-dropzone ${isDragOver ? 'drag-over' : ''} ${files.length > 0 ? 'has-files' : ''
          }`}
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragOver(false);
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="dropzone-content">
          <i className="pi pi-cloud-upload"></i>
          <div className="dropzone-text">
            <h4>Drag & drop files here</h4>
            <p>
              or <span className="browse-link">browse</span> to choose files
            </p>
          </div>
          <small>Supported formats: {acceptedFiles}</small>
          {showSizeLimit && (
            <>
              <br />
              <small style={{ color: '#FF0000' }}>
                Please upload file less than 8MB
              </small>
            </>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={acceptedFiles}
          onChange={handleFileChange}
          className="file-input"
        />
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="error-messages">
          <i className="pi pi-exclamation-triangle"></i>
          <div className="error-content">
            <strong>Upload Error</strong>
            {errors.map((error, index) => (
              <div key={index} className="error-message">
                {error}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Files List */}
      {files.length > 0 && (
        <div className="upload-queue">
          <div className="queue-header">
            <h4>Selected Files ({files.length})</h4>
            {/* <span className="files-counter">{files.length} files selected</span> */}
          </div>

          <div className="files-list">
            {files.map((file, index) => {
              const error = hasError(file.name);
              return (
                <div
                  key={index}
                  className={`file-item ${error ? 'has-error' : ''}`}
                >
                  <div className="file-icon">
                    <i className={getFileIcon(file.name)}></i>
                  </div>

                  <div className="file-details">
                    <div className="file-name">
                      {file.name}
                      {error && (
                        <span className="error-badge">
                          <i className="pi pi-exclamation-triangle"></i>
                          Error
                        </span>
                      )}
                    </div>
                    <div className="file-info">
                      <span className="file-size">
                        {formatFileSize(file.size)}
                      </span>
                      <span className="file-type">
                        {file?.name?.split('.')?.pop()?.toUpperCase()}
                      </span>
                    </div>
                    {error && (
                      <div className="file-error">{getError(file?.name)}</div>
                    )}
                  </div>

                  <div className="file-actions">
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(index);
                      }}
                      title="Remove file"
                    >
                      <i className="pi pi-times"></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {errors.length > 0 && (
            <div className="upload-errors-summary">
              <i className="pi pi-exclamation-circle"></i>
              <span>
                {errors.length} file(s) failed to upload due to validation
                errors
              </span>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {files.length > 0 && (
        <div className="action-buttons">
          {multiple && (
            <button
              type="button"
              className="upload-button secondary"
              onClick={clearFiles}
            >
              <i className="pi pi-trash"></i>
              Clear All
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FileUploader;
